import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ALL_WORK_ORDER_PRIORITIES } from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import {
  assignmentsApi,
  organisationApi,
  samplesApi,
  specificationsApi,
  type AssignableTest,
} from '../../api/endpoints.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DateField,
  Form,
  FormSection,
  PageHeader,
  Panel,
  ReadOnlyField,
  SearchField,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
} from '../../components/ui/index.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDateTime } from '../../components/format.js';
import { toneForSampleStatus } from '../samples/status.js';
import { useSampleTypes } from '../samples/sample-type-fields.js';
import { currentLocale } from '../../i18n/index.js';
import { ReadinessBanner } from './ReadinessBanner.js';
import {
  requestedTestLabel,
  requestedTestName,
  requestedTestStandard,
} from '../samples/requested-tests.js';

/**
 * Assign a test to a sample — module C5.
 *
 * THE TESTS COME FROM THE SAMPLE'S WORK ORDER (D316), never the catalogue:
 * only its services that may be chosen and are not yet assigned are offered,
 * and the server refuses anything else. A sample whose requested tests were
 * recorded is offered only those (D218).
 *
 * ONLY AN ACCEPTED SAMPLE RECEIVES WORK. Samples that cannot are shown
 * disabled with their state, so a person sees why rather than a missing row;
 * the backend refuses them regardless, naming the decision and its reason.
 *
 * ASSIGNING AGAIN IS ANSWERED, NOT DISMISSED. A test already assigned is
 * listed with its work; assigning it a second time is a deliberate act that
 * asks why (Phase 8). A retest of existing work is a new ATTEMPT on that
 * assignment, and the warning says that too.
 *
 * The test carries its own method: the standard is the work order's, and the
 * specialised template the catalogue's. The project specification or client
 * reference is optional context and decides nothing about how the work is
 * entered; no field, limit or formula follows from anything on this form.
 */
export function AssignTestPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const locale = currentLocale();
  const [params] = useSearchParams();
  const sampleTypes = useSampleTypes();

  const [sampleSearch, setSampleSearch] = useState('');
  const [sampleId, setSampleId] = useState<string | null>(params.get('sampleId'));
  const [pieceId, setPieceId] = useState<string | null>(null);
  const [workOrderServiceId, setWorkOrderServiceId] = useState<string | null>(null);
  const [specificationId, setSpecificationId] = useState<string | null>(null);
  const [specificationText, setSpecificationText] = useState('');
  const [departmentId, setDepartmentId] = useState<string | null>(null);
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [priority, setPriority] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [instructions, setInstructions] = useState('');
  const [duplicateReason, setDuplicateReason] = useState('');
  const [isDuplicateWarning, setIsDuplicateWarning] = useState(false);

  const samples = useQuery({
    queryKey: ['samples', 'assignable', sampleSearch],
    queryFn: () => samplesApi.list({ pageSize: 25, ...(sampleSearch ? { search: sampleSearch } : {}) }),
  });
  const chosenSample = useQuery({
    queryKey: ['sample', sampleId],
    queryFn: () => samplesApi.get(sampleId as string),
    enabled: sampleId !== null,
  });
  const pieces = useQuery({
    queryKey: ['sample', sampleId, 'pieces'],
    queryFn: () => samplesApi.pieces(sampleId as string),
    enabled: sampleId !== null,
  });
  const scope = useQuery({
    queryKey: ['sample', sampleId, 'assignable-tests'],
    queryFn: () => assignmentsApi.assignableTests(sampleId as string),
    enabled: sampleId !== null,
  });
  const specifications = useQuery({
    queryKey: ['specifications', 'active'],
    queryFn: () => specificationsApi.list({ pageSize: 100 }),
  });
  const departments = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: () => organisationApi.listDepartments(false),
  });
  const assignees = useQuery({
    queryKey: ['assignees'],
    queryFn: () => assignmentsApi.assignees(),
  });

  const sampleRecord = chosenSample.data?.id === sampleId ? chosenSample.data : undefined;
  const scoped = scope.data?.sampleId === sampleId ? scope.data : undefined;
  const tests = scoped?.tests ?? [];

  /** Live work for this test on the chosen target: the piece, or the whole sample. */
  const workFor = (test: AssignableTest) =>
    test.liveAssignments.filter((work) => (work.pieceId ?? null) === pieceId);
  const assignable = tests.filter((test) => test.availability === 'available');
  const remaining = assignable.filter((test) => workFor(test).length === 0);
  const alreadyAssigned = assignable.filter((test) => workFor(test).length > 0);
  const chosenTest = tests.find((test) => test.workOrderServiceId === workOrderServiceId);
  /** Offered: what remains, and a test being assigned again on purpose. */
  const offered =
    chosenTest && !remaining.includes(chosenTest) ? [...remaining, chosenTest] : remaining;

  const testLabel = (test: AssignableTest) =>
    requestedTestLabel(
      requestedTestName(test.nameAr, test.nameEn, locale),
      requestedTestStandard(test.referenceStandard, test.standardVersion),
      test.code ?? t('common.notRecorded'),
    );

  const create = useMutation({
    mutationFn: () =>
      assignmentsApi.create({
        sampleId,
        catalogueItemId: chosenTest?.catalogueItemId,
        workOrderServiceId,
        ...(pieceId ? { pieceId } : {}),
        ...(specificationId ? { specificationId } : {}),
        ...(specificationText.trim() ? { specificationText: specificationText.trim() } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(assignedTo ? { assignedTo } : {}),
        ...(priority ? { priority } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
        ...(isDuplicateWarning && duplicateReason.trim()
          ? { duplicateReason: duplicateReason.trim() }
          : {}),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['sample', sampleId] });
      navigate(`/work/${created.id}`);
    },
    onError: (error) => {
      if (
        error instanceof ApiError &&
        error.code === 'DUPLICATE_RECORD' &&
        error.details.some((detail) => detail.code === 'duplicate_assignment')
      ) {
        setIsDuplicateWarning(true);
      }
    },
  });

  /** The refusal's own reason, when the catalogue of field messages has one. */
  const refusalDetail =
    create.error instanceof ApiError
      ? create.error.details
          .map((detail) => detail.code)
          .find((code) => code && i18n.exists(`fieldErrors.${code}`))
      : undefined;

  const sampleOptions = [
    ...(chosenSample.data && !(samples.data?.data ?? []).some((s) => s.id === chosenSample.data.id)
      ? [chosenSample.data]
      : []),
    ...(samples.data?.data ?? []),
  ].map((sample) => ({
    value: sample.id,
    label: sample.sampleNumber,
    description: t(`status.sample.${sample.status}`),
    isDisabled: !sample.mayBeAssignedTests,
  }));

  const activeSpecifications = specifications.data?.data ?? [];
  const duplicateBlocked = isDuplicateWarning && duplicateReason.trim().length < 3;
  const mayBeAssigned = sampleRecord?.mayBeAssignedTests ?? false;

  const chooseTest = (value: string | null, again = false) => {
    setWorkOrderServiceId(value);
    setIsDuplicateWarning(again);
    create.reset();
  };

  /** Why a work-order test is not offered, as the side summary marks it. */
  const markFor = (test: AssignableTest): string | null => {
    if (test.availability === 'not_a_catalogue_test') return t('assign.requestedTestNotCatalogue');
    if (test.availability === 'cancelled') return t('workOrderServiceState.cancelled');
    if (test.availability === 'not_requested_for_sample') return t('assign.notRequestedForSample');
    if (workFor(test).length > 0) return t('assign.alreadyAssigned');
    return null;
  };

  return (
    <>
      <PageHeader
        title={t('assign.title')}
        subtitle={t('assign.subtitle')}
        breadcrumbs={[{ label: t('nav.laboratoryWork'), to: '/work' }, { label: t('assign.title') }]}
      />

      {/*
        The form, with the chosen sample's facts beside it on a wide screen
        (D278). The facts are read from the sample record and its work order;
        nothing in the side column is a field of the assignment.
      */}
      <div className="grid grid-cols-1 items-start gap-gutter lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0">
          <Form
            onSubmit={() => create.mutate()}
            actions={
              <ActionBar>
                <ActionButton priority="tertiary" onPress={() => navigate('/work')}>
                  {t('common.cancel')}
                </ActionButton>
                <ActionButton
                  priority="primary"
                  type="submit"
                  isDisabled={!sampleId || !mayBeAssigned || !chosenTest || duplicateBlocked}
                  isLoading={create.isPending}
                >
                  {isDuplicateWarning ? t('assign.assignAgain') : t('assign.submit')}
                </ActionButton>
              </ActionBar>
            }
          >
            <FormSection title={t('assign.sampleSection')} description={t('assign.sampleSectionHint')}>
              <SearchField
                value={sampleSearch}
                onChange={setSampleSearch}
                label={t('assign.sampleSearchLabel')}
                placeholder={t('samples.searchPlaceholder')}
              />
              <SelectField
                label={t('assign.sampleLabel')}
                value={sampleId}
                onChange={(value) => {
                  setSampleId(value);
                  setPieceId(null);
                  // A work-order test belongs to ONE sample: another sample chooses again.
                  chooseTest(null);
                }}
                isRequired
                options={sampleOptions}
                placeholder={t('assign.samplePlaceholder')}
              />
              {chosenSample.data && !chosenSample.data.mayBeAssignedTests ? (
                <Alert
                  tone="warning"
                  title={t('samples.mayNotBeTestedTitle')}
                  description={t('samples.mayNotBeTestedBody')}
                />
              ) : null}
              {(pieces.data ?? []).length > 0 ? (
                <SelectField
                  label={t('assign.pieceLabel')}
                  description={t('assign.pieceHint')}
                  value={pieceId}
                  onChange={(value) => {
                    setPieceId(value);
                    chooseTest(null);
                  }}
                  placeholder={t('assign.wholeSample')}
                  options={(pieces.data ?? []).map((piece) => ({ value: piece.id, label: piece.pieceCode }))}
                />
              ) : null}
            </FormSection>

            <FormSection title={t('assign.testSection')} description={t('assign.testSectionHint')} columns={1}>
              {!sampleId ? (
                <p className="text-body text-default-600">{t('assign.chooseSampleFirst')}</p>
              ) : scope.error ? (
                <ErrorNotice error={scope.error} />
              ) : !scoped ? (
                <p className="text-body text-default-600">{t('common.loading')}</p>
              ) : !scoped.workOrder ? (
                <Alert tone="warning" title={t('assign.noWorkOrderTitle')} description={t('assign.noWorkOrderBody')} />
              ) : assignable.length === 0 ? (
                <Alert tone="info" title={t('assign.noWorkOrderTests')} />
              ) : (
                <>
                  {remaining.length === 0 && !isDuplicateWarning ? (
                    <Alert tone="info" title={t('assign.allAssigned')} />
                  ) : (
                    <SelectField
                      label={t('assign.testLabel')}
                      description={t('assign.testHint')}
                      value={workOrderServiceId}
                      onChange={(value) => chooseTest(value, isDuplicateWarning && value === workOrderServiceId)}
                      isRequired
                      placeholder={t('assign.testPlaceholder')}
                      options={offered.map((test) => ({
                        value: test.workOrderServiceId,
                        label: testLabel(test),
                        ...(workFor(test).length > 0 ? { description: t('assign.alreadyAssigned') } : {}),
                      }))}
                    />
                  )}

                  {chosenTest ? (
                    <div className="flex flex-col gap-stack rounded-medium border border-default-200 p-4">
                      <ReadOnlyField label={t('assign.testFact')}>{testLabel(chosenTest)}</ReadOnlyField>
                      <ReadOnlyField label={t('assign.standardFact')}>
                        <span dir="ltr">
                          {requestedTestStandard(chosenTest.referenceStandard, chosenTest.standardVersion) ??
                            t('common.notRecorded')}
                        </span>
                      </ReadOnlyField>
                      {!chosenTest.catalogueItemActive ? (
                        <p className="text-body text-default-600">{t('assign.archivedTest')}</p>
                      ) : null}
                      <ReadinessBanner readiness={chosenTest.templateReadiness} />
                    </div>
                  ) : null}

                  {alreadyAssigned.length > 0 ? (
                    <ReadOnlyField label={t('assign.alreadyAssignedList')}>
                      <ul className="flex flex-col gap-2">
                        {alreadyAssigned.map((test) => (
                          <li key={test.workOrderServiceId} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="text-body text-default-800">{testLabel(test)}</span>
                            {workFor(test).map((work) => (
                              <Link key={work.id} to={`/work/${work.id}`} className="text-body text-primary-800 underline">
                                {t('assign.openWork')}
                              </Link>
                            ))}
                            <ActionButton
                              priority="tertiary"
                              size="sm"
                              onPress={() => chooseTest(test.workOrderServiceId, true)}
                            >
                              {t('assign.assignAgainAction')}
                            </ActionButton>
                          </li>
                        ))}
                      </ul>
                    </ReadOnlyField>
                  ) : null}
                </>
              )}
            </FormSection>

            <FormSection title={t('assign.whoSection')} description={t('assign.whoSectionHint')}>
              <SelectField
                label={t('assign.departmentLabel')}
                value={departmentId}
                onChange={setDepartmentId}
                placeholder={t('common.none')}
                options={(departments.data?.data ?? []).map((department) => ({
                  value: department.id,
                  label: department.name || (department.code ?? ''),
                }))}
              />
              <SelectField
                label={t('assign.assigneeLabel')}
                description={t('assign.assigneeHint')}
                value={assignedTo}
                onChange={setAssignedTo}
                placeholder={t('work.unassigned')}
                options={(assignees.data ?? []).map((person) => ({
                  value: person.id,
                  label: person.displayName,
                  description: t(`roles.${person.role}`, { defaultValue: person.role }),
                }))}
              />
              <SelectField
                label={t('work.priorityColumn')}
                value={priority}
                onChange={setPriority}
                placeholder={t('assign.priorityDefault')}
                options={ALL_WORK_ORDER_PRIORITIES.map((value) => ({
                  value,
                  label: t(`workOrderPriority.${value}`),
                }))}
              />
              <DateField
                label={t('assign.dueDateLabel')}
                description={t('assign.dueDateHint')}
                value={dueDate}
                onChange={setDueDate}
              />
              <TextAreaField
                label={t('assign.instructionsLabel')}
                value={instructions}
                onChange={setInstructions}
                rows={3}
              />
            </FormSection>

            {/*
              Optional context, never the method (D317). The register select
              appears only when the laboratory keeps a register at all.
            */}
            <FormSection title={t('assign.referencesSection')} description={t('assign.referencesSectionHint')}>
              <TextField
                label={t('assign.specificationTextLabel')}
                description={t('assign.specificationTextHint')}
                value={specificationText}
                onChange={setSpecificationText}
              />
              {activeSpecifications.length > 0 ? (
                <SelectField
                  label={t('assign.specificationLabel')}
                  description={t('assign.specificationHint')}
                  value={specificationId}
                  onChange={setSpecificationId}
                  placeholder={t('common.none')}
                  options={activeSpecifications.map((item) => ({
                    value: item.id,
                    label: item.code,
                    ...(item.issuingBody ? { description: item.issuingBody } : {}),
                  }))}
                />
              ) : null}
            </FormSection>

            {isDuplicateWarning ? (
              <div className="flex flex-col gap-stack">
                <Alert
                  tone="warning"
                  title={t('assign.duplicateTitle')}
                  description={t('assign.duplicateBody')}
                />
                <TextAreaField
                  label={t('assign.duplicateReasonLabel')}
                  value={duplicateReason}
                  onChange={setDuplicateReason}
                  isRequired
                  rows={2}
                />
              </div>
            ) : create.error ? (
              <div className="flex flex-col gap-stack">
                <ErrorNotice error={create.error} />
                {refusalDetail ? (
                  <p className="text-body text-danger-700">{t(`fieldErrors.${refusalDetail}`)}</p>
                ) : null}
              </div>
            ) : null}
          </Form>
        </div>

        <aside className="min-w-0">
          <Panel title={t('assign.chosenSampleTitle')}>
            {sampleRecord ? (
              <div className="flex flex-col gap-stack">
                <ReadOnlyField label={t('samples.sampleId')}>
                  <Link to={`/samples/${sampleRecord.id}`} className="measure hover:underline" dir="ltr">
                    {sampleRecord.sampleNumber}
                  </Link>
                </ReadOnlyField>
                <ReadOnlyField label={t('common.status')}>
                  <StatusBadge
                    label={t(`status.sample.${sampleRecord.status}`)}
                    tone={toneForSampleStatus(sampleRecord.status)}
                    size="sm"
                  />
                </ReadOnlyField>
                <ReadOnlyField label={t('assign.receivedFact')}>
                  {formatDateTime(sampleRecord.receivedAt, { locale }) ?? sampleRecord.receivedAt}
                </ReadOnlyField>
                <ReadOnlyField label={t('assign.workOrderFact')}>
                  {sampleRecord.jobNumber ? (
                    <span className="measure" dir="ltr">
                      {sampleRecord.jobNumber}
                    </span>
                  ) : (
                    t('assign.noWorkOrderFact')
                  )}
                </ReadOnlyField>
                <ReadOnlyField label={t('samples.sampleTypeLabel')}>
                  {sampleTypes.nameOf(sampleRecord.sampleType)}
                </ReadOnlyField>
                {scoped?.workOrder ? (
                  <ReadOnlyField label={t('assign.workOrderTestsFact')}>
                    {tests.length === 0 ? (
                      t('assign.noWorkOrderTestsFact')
                    ) : (
                      <ul className="flex flex-col gap-1">
                        {tests.map((test) => {
                          const mark = markFor(test);
                          return (
                            <li key={test.workOrderServiceId} className="text-body text-default-800">
                              {testLabel(test)}
                              {mark ? <span className="block text-default-600">{mark}</span> : null}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </ReadOnlyField>
                ) : null}
                {scoped?.requestedTestsRecorded ? (
                  <ReadOnlyField label={t('assign.requestedForSampleFact')}>
                    <ul className="flex flex-col gap-1">
                      {tests
                        .filter((test) => test.requestedForSample)
                        .map((test) => (
                          <li key={test.workOrderServiceId} className="text-body text-default-800">
                            {testLabel(test)}
                          </li>
                        ))}
                    </ul>
                  </ReadOnlyField>
                ) : null}
              </div>
            ) : (
              <p className="text-body text-default-600">{t('assign.noSampleChosen')}</p>
            )}
          </Panel>
        </aside>
      </div>
    </>
  );
}
